import { css } from '@emotion/react';
import { Stack, SxProps } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';
import { FormState, useFormContext } from 'react-hook-form';
import { ErrorText, FormLoadingButton } from '../../../../components/Form/FormComponents';
import { FormFieldFactory } from '../../../../components/Form/FormFieldFactory';
import { simpleTextField } from '../../../../data-fields/CommonFields';
import { CustomKpiTemplateFormValues } from '../../../../schemas/kpi-template.schema';
import { useSaveCustomKpiTemplate } from './hooks/useSaveCustomKpiTemplate';

const fieldCss = css`
  min-width: 10rem;
  overflow-x: clip;
  & * {
    max-width: 100%;
  }
  &&& .MuiInputBase-root input {
    font-size: 1.25rem;
    color: var(--mui-palette-secondary-main);
    @supports (field-sizing: content) {
      field-sizing: content;
    }
  }
`;

function TemplateNameField() {
  const { formState } = useFormContext<CustomKpiTemplateFormValues>();

  const nameField = useMemo(
    () =>
      simpleTextField({
        key: 'name',
        label: '',
        required: true,
        placeholder: 'Template Name',
        autoFocus: true,
      }),
    []
  );

  return (
    <div css={fieldCss}>
      <div>
        <FormFieldFactory formField={nameField} />
        <FloatingErrorText sx={{ mt: '-0.75rem' }}>{getFieldError(formState, 'name')}</FloatingErrorText>
      </div>
    </div>
  );
}

interface TemplateFormHeaderProps {
  isDirty: boolean;
}

export function TemplateFormHeader({ isDirty }: TemplateFormHeaderProps) {
  const { save, isUpdate, isPending } = useSaveCustomKpiTemplate();

  return (
    <Stack display='grid' gridTemplateColumns='1fr auto' alignItems='center' gap='1rem'>
      <TemplateNameField />
      <FormLoadingButton onClick={save} disabled={isPending || !isDirty} loading={isPending} size='medium'>
        {isUpdate ? 'Update Template' : 'Save Template'}
      </FormLoadingButton>
    </Stack>
  );
}

function FloatingErrorText({ children, sx = {} }: PropsWithChildren & { sx?: SxProps }) {
  return (
    <ErrorText sx={{ height: 0, overflow: 'visible', transform: 'translateY(0.5rem)', ...sx }}>
      {children}
    </ErrorText>
  );
}

function getFieldError(
  formState: FormState<CustomKpiTemplateFormValues>,
  field: keyof CustomKpiTemplateFormValues
): string | undefined {
  return formState.isSubmitted ? formState.errors[field]?.message : undefined;
}
